import React from "react";
import { Check, SlidersHorizontal } from "lucide-react";
import { usePersonalization, PROFILES, ROUTE_LABELS } from "@/lib/personalization";
import { trackEvent } from "@/lib/analytics";

const K = { fontFamily: "Kanit, Arial, sans-serif" };

// Contextual sub-navigation under the main header. Always visible; shows the current route and lets the visitor change it.
export default function RouteSelector({ transparent = false }) {
  const { profile, p, setProfile, reopen } = usePersonalization();
  const routes = PROFILES.map((id) => ({ id, title: ROUTE_LABELS[id] }));

  const onUpdate = () => {
    trackEvent("personalize_reopen", { from: profile || "neutral", placement: "route_selector" });
    reopen();
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const base = transparent ? "text-white/80 hover:text-white" : "text-[#6B4954] hover:text-[#451C25]";
  const activeCls = transparent ? "bg-white/95 text-[#451C25]" : "bg-white text-[#451C25] border border-[#897756]/60";
  const label = transparent ? "text-white/60" : "text-[#897756]";

  return (
    <nav
      id="routes"
      data-testid="route-selector"
      aria-label={p.route_headline}
      className={`border-t ${transparent ? "border-white/15 bg-[#2B1017]/30 backdrop-blur-md" : "border-[#451C25]/10 bg-[#F5EFE7]/95"} transition-colors duration-500`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 flex flex-col lg:flex-row lg:items-center gap-0 lg:gap-5 py-1 lg:py-0 lg:h-11">
        <div className="flex items-center justify-between gap-2">
          <p data-testid="route-selector-headline" className={`text-[9px] lg:text-[10px] tracking-[0.22em] uppercase ${label} pt-1 lg:pt-0`} style={K}>{p.route_headline}</p>
          <button type="button" data-testid="route-update-link" onClick={onUpdate} aria-label={p.route_update_label} title={p.route_update_label} className={`lg:hidden shrink-0 w-6 h-6 inline-flex items-center justify-center ${base}`}>
            <SlidersHorizontal size={14} aria-hidden="true" />
          </button>
        </div>
        <div role="group" aria-label={p.route_headline} data-testid="route-options" className="flex-1 min-w-0 grid grid-cols-3 lg:flex items-center gap-1">
          {routes.map((r) => {
            const active = r.id === profile;
            return (
              <button
                key={r.id}
                type="button"
                data-testid={`route-${r.id}`}
                aria-pressed={active}
                onClick={() => setProfile(r.id, "route_selector")}
                className={`min-w-0 inline-flex justify-center lg:justify-start items-center gap-1 min-h-8 lg:h-9 px-1 lg:px-3 text-[11px] sm:text-[12px] lg:text-[12.5px] transition-colors ${active ? activeCls : base}`}
                style={{ ...K, fontWeight: active ? 600 : 500 }}
              >
                {active && <Check size={12} className="text-[#897756]" aria-hidden="true" />}
                {r.title}
                {active && <span className="sr-only">(selected)</span>}
              </button>
            );
          })}
        </div>
        <button type="button" data-testid="route-update-link-desktop" onClick={onUpdate} className={`hidden lg:inline-flex shrink-0 h-9 items-center text-[10px] tracking-[0.16em] uppercase underline underline-offset-4 ${base}`} style={K}>
          {p.route_update_label}
        </button>
      </div>
    </nav>
  );
}
